import { initializeApp } from 'firebase/app';
import { Auth, getAuth } from 'firebase/auth';
import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Firestore,
  FirestoreDataConverter,
  Query,
  Unsubscribe,
  collection as coll,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  initializeFirestore,
  onSnapshot,
  persistentLocalCache,
  persistentMultipleTabManager,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { useMemo, useRef } from 'react';
import { Observable } from 'rxjs';
import { Entity } from '../dto/entity';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});

export interface FirebaseInterface {
  auth: Auth;
  store: Firestore;
  translateError: (error: string) => string;

  // DB methods
  getDocument: <T extends DocumentData>(path: string, id: string) => Promise<T>;
  getDocumentConverted: <T extends DocumentData, U>(
    path: string,
    id: string,
    converter: FirestoreDataConverter<U, T>,
  ) => Promise<U>;
  setDocument: <T extends DocumentData>(path: string, data: T, id?: string, merge?: boolean) => Promise<T & Entity>;
  deleteDocument: (path: string, id: string) => Promise<void>;
  document: (path: string, id?: string) => DocumentReference;
  collection: (path: string, ...pathSegments: string[]) => CollectionReference;

  // query helpers
  queryRun: <T>(query: Query<DocumentData, DocumentData>) => Promise<T[]>;
  querySubscribe: <T>(query: Query<DocumentData, DocumentData>, callback: (data: T[]) => void) => Unsubscribe;
  queryToObservable: <T>(query: Query<DocumentData, DocumentData>) => [Unsubscribe, Observable<T[]>];
  deleteQuery: (query: Query<DocumentData, DocumentData>) => Promise<void>;

  // helpers
  waitIfOnline: <T>(task: Promise<T>) => Promise<T | undefined>;
}

export function useFirebase(): FirebaseInterface {
  const auth = useRef<Auth>(getAuth(app));
  const store = useRef<Firestore>(getFirestore(app));
  const env = import.meta.env.VITE_ENV;

  function translateError(error?: string): string {
    switch (error) {
      case 'auth/invalid-credential':
        return 'Invalid credentials';

      case 'auth/invalid-email':
        return 'Invalid mail';

      case 'auth/email-already-in-use':
        return 'Account already exists';

      case 'auth/too-many-requests':
        return 'Too many requests';

      case 'auth/network-request-failed':
        return 'No connection';

      case 'auth/weak-password':
        return 'Weak password';

      case 'auth/invalid-action-code':
        return 'Invalid reset code';

      case 'auth/expired-action-code':
        return 'Expired reset code';

      default:
        if (error?.includes('offline')) return 'No connection';
        if (error?.includes('permissions')) return 'Missing permissions';

        return error ?? 'Unknown error';
    }
  }

  // DB methods
  function getDocument<T extends DocumentData>(path: string, id: string): Promise<T> {
    const docRef = document(path, id);
    return getDoc(docRef).then((s) => s.data() as T);
  }

  function getDocumentConverted<T extends DocumentData, U>(
    path: string,
    id: string,
    converter: FirestoreDataConverter<U, T>,
  ): Promise<U> {
    const docRef = document(path, id).withConverter(converter);
    return getDoc(docRef).then((s) => s.data() as U);
  }

  async function setDocument<T extends DocumentData>(
    path: string,
    data: T,
    id?: string,
    merge?: boolean,
  ): Promise<T & Entity> {
    const docRef = document(path, id);
    const doc = { id: docRef.id, ...data };
    await setDoc(docRef, doc, { merge: merge ?? false });
    return doc;
  }

  function deleteDocument(path: string, id: string): Promise<void> {
    const docRef = document(path, id);
    return deleteDoc(docRef);
  }

  function document(path: string, id?: string): DocumentReference {
    const collRef = collection(path);
    return id ? doc(collRef, id) : doc(collRef);
  }

  function collection(path: string, ...pathSegments: string[]): CollectionReference {
    return coll(store.current, 'environments', env, path, ...pathSegments);
  }

  // query helpers
  async function queryRun<T>(query: Query<DocumentData, DocumentData>): Promise<T[]> {
    return getDocs(query).then((snapshot) => snapshot.docs.map((d) => d.data() as T));
  }

  function querySubscribe<T>(query: Query<DocumentData, DocumentData>, callback: (data: T[]) => void): Unsubscribe {
    const [unsubscribe, observable$] = queryToObservable<T>(query);

    observable$.subscribe((r) => callback(r));

    return unsubscribe;
  }

  function queryToObservable<T>(query: Query<DocumentData, DocumentData>): [Unsubscribe, Observable<T[]>] {
    let unsubscribe = () => {};
    const observable$ = new Observable<T[]>((observer) => {
      unsubscribe = onSnapshot(query, (snapshot) => observer.next(snapshot.docs.map((d) => d.data() as T)));
    });

    return [unsubscribe, observable$];
  }

  async function deleteQuery(query: Query<DocumentData, DocumentData>): Promise<void> {
    const docs = await getDocs(query);

    const batch = writeBatch(store.current);
    docs.forEach((doc) => batch.delete(doc.ref));
    await batch.commit();
  }

  // helpers
  async function waitIfOnline<T>(task: Promise<T>): Promise<T | undefined> {
    if (navigator.onLine) return task;
  }

  return useMemo(
    () => ({
      auth: auth.current,
      store: store.current,
      translateError,
      getDocument,
      getDocumentConverted,
      setDocument,
      deleteDocument,
      document,
      collection,
      queryRun,
      querySubscribe,
      queryToObservable,
      deleteQuery,
      waitIfOnline,
    }),
    [auth, store],
  );
}
